<template>
  <div class="tcf-student-form">
    <b-card no-body class="student-card mb-2">
      <b-card-body class="p-2 p-md-3">
        <div class="form-header d-flex flex-wrap align-items-center mb-2">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="back-btn mr-1"
            @click="goBack"
          >
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            {{ $t("Back") }}
          </b-button>
          <div>
            <h4 class="mb-0 form-page-title">
              {{ pageTitle }}
            </h4>
            <small class="text-muted">{{ pageSubtitle }}</small>
          </div>
        </div>

        <div v-if="pageLoading" class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1 mb-0">{{ $t("Loading…") }}</p>
        </div>

        <b-form v-else @submit.prevent="saveStudent" class="admission-form">
          <div class="section-panel">
            <div class="section-panel__head">
              <span class="section-panel__icon">
                <feather-icon icon="UserIcon" size="16" />
              </span>
              <h5 class="section-panel__title">
                {{ $t("Basic Information") }}
              </h5>
            </div>
            <div class="section-panel__body section-panel__body--photo">
              <div class="photo-upload">
                <div
                  class="photo-upload__avatar-wrap"
                  role="button"
                  tabindex="0"
                  @click="triggerPhotoUpload"
                  @keydown.enter.prevent="triggerPhotoUpload"
                >
                  <b-avatar
                    :src="photoPreviewSrc"
                    size="120"
                    variant="light"
                    class="photo-upload__avatar"
                  />
                  <span class="photo-upload__badge" aria-hidden="true">
                    <feather-icon icon="CameraIcon" size="15" />
                  </span>
                  <div v-if="photoLoading" class="photo-upload__loading">
                    <b-spinner small variant="light" />
                  </div>
                </div>

                <div class="photo-upload__meta">
                  <p class="photo-upload__title">{{ $t("Student Photo") }}</p>
                  <p class="photo-upload__hint">
                    {{ $t("JPG or PNG, max 5MB") }}
                  </p>
                  <div class="photo-upload__actions">
                    <button
                      type="button"
                      class="photo-upload__link"
                      :disabled="photoLoading"
                      @click="triggerPhotoUpload"
                    >
                      {{
                        form.student.picture
                          ? $t("Change photo")
                          : $t("Upload photo")
                      }}
                    </button>
                    <button
                      v-if="form.student.picture"
                      type="button"
                      class="photo-upload__link photo-upload__link--danger"
                      :disabled="photoLoading"
                      @click="removePhoto"
                    >
                      {{ $t("Remove") }}
                    </button>
                  </div>
                </div>

                <input
                  ref="photoInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/*"
                  hidden
                  @change="onPhotoSelected"
                />
              </div>
            </div>
            <b-row class="section-panel__body section-panel__body--compact">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Student Name')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.student.name"
                    class="form-input-custom"
                    :placeholder="$t('Enter student name')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Father Name')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.student.father_name"
                    class="form-input-custom"
                    :placeholder="$t('Enter father name')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Gender')" label-class="field-label">
                  <v-select
                    v-model="form.student.gender"
                    :options="genderOptions"
                    :reduce="(o) => o.value"
                    label="label"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Please select gender')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Class')" label-class="field-label">
                  <v-select
                    v-model="selectedClassId"
                    :options="classOptions"
                    :reduce="(o) => o.id"
                    label="label"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Please select class')"
                    @input="onClassChange"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Section')" label-class="field-label">
                  <v-select
                    v-model="selectedSectionId"
                    :options="sectionOptions"
                    :reduce="(o) => o.id"
                    label="label"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="
                      selectedClassId
                        ? $t('Please select section')
                        : $t('Select class first')
                    "
                    :disabled="!selectedClassId"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Student\'s Contact')"
                  label-class="field-label field-label--required"
                >
                  <b-form-input
                    v-model.trim="form.student.contact_student"
                    class="form-input-custom"
                    :placeholder="$t('Enter primary contact number')"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('WhatsApp Number')"
                  label-class="field-label field-label--required"
                >
                  <b-form-input
                    v-model.trim="form.student.contact_father"
                    class="form-input-custom"
                    :placeholder="$t('Enter WhatsApp number')"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Parent\'s Contact')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.student.contact_mother"
                    class="form-input-custom"
                    :placeholder="$t('Enter parent contact number')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Student\'s CNIC No.')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.studentCnic"
                    class="form-input-custom"
                    :placeholder="$t('Enter CNIC number')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Email Address')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.student.emailAddress"
                    class="form-input-custom"
                    :placeholder="$t('Enter email')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Student Category')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.student.last_result"
                    class="form-input-custom"
                    :placeholder="$t('Enter category')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="section-panel">
            <div class="section-panel__head">
              <span class="section-panel__icon">
                <feather-icon icon="MapPinIcon" size="16" />
              </span>
              <h5 class="section-panel__title">
                {{ $t("Location & Branch") }}
              </h5>
            </div>
            <b-row class="section-panel__body">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Region')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.region"
                    class="form-input-custom"
                    :placeholder="$t('Enter region')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Area')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.area"
                    class="form-input-custom"
                    :placeholder="$t('Enter area')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Branch Code')"
                  label-class="field-label field-label--required"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.branchCode"
                    class="form-input-custom"
                    :placeholder="$t('Enter branch code')"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('School')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.school"
                    class="form-input-custom"
                    :placeholder="$t('Enter school name')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Institute Name')"
                  label-class="field-label field-label--required"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.instituteName"
                    class="form-input-custom"
                    :placeholder="$t('Enter institute name')"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="section-panel">
            <div class="section-panel__head">
              <span class="section-panel__icon">
                <feather-icon icon="BookIcon" size="16" />
              </span>
              <h5 class="section-panel__title">
                {{ $t("Academic Information") }}
              </h5>
            </div>
            <b-row class="section-panel__body">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Federal Board Registration No')"
                  label-class="field-label field-label--required"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.federalRegNo"
                    class="form-input-custom"
                    :placeholder="$t('Enter federal board registration no')"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Matric Roll No.')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.matricRollNo"
                    class="form-input-custom"
                    :placeholder="$t('Enter matric roll number')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Matric Board')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.matricBoard"
                    class="form-input-custom"
                    :placeholder="$t('Enter matric board')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Matric %')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.matricPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter matric percentage')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Matric Grade')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.matricGrade"
                    class="form-input-custom"
                    :placeholder="$t('Enter matric grade')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('9th %')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.ninthPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter 9th percentage')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('HSST %')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.hsstPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter HSST percentage')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Overall Score (HSST + Matric)')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.overallPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter overall score')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('HSST Status')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.hsstStatus"
                    class="form-input-custom"
                    :placeholder="$t('Enter HSST status')"
                  />
                </b-form-group>
              </b-col>
              <!-- <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Inter Faculty')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.interFaculty"
                    class="form-input-custom"
                    :placeholder="$t('Enter inter faculty')"
                  />
                </b-form-group>
              </b-col> -->
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Intermediate Board')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.intermediateBoard"
                    class="form-input-custom"
                    :placeholder="$t('Enter intermediate board')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('College')" label-class="field-label">
                  <b-form-input
                    v-model.trim="form.studentDetails.college"
                    class="form-input-custom"
                    :placeholder="$t('Enter college')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('1st Year Roll Number')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.firstYearRollNo"
                    class="form-input-custom"
                    :placeholder="$t('Enter 1st year roll number')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('1st Year %')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.firstYearPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter 1st year percentage')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('1st Year Paper Passed')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.paperPassed"
                    class="form-input-custom"
                    :placeholder="$t('Enter papers passed')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Second Year Roll No')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.secondYearRollNo"
                    class="form-input-custom"
                    :placeholder="$t('Enter 2nd year roll number')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Second Year %')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.secondYearPerc"
                    class="form-input-custom"
                    type="number"
                    step="0.01"
                    :placeholder="$t('Enter 2nd year percentage')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="section-panel">
            <div class="section-panel__head">
              <span class="section-panel__icon">
                <feather-icon icon="AwardIcon" size="16" />
              </span>
              <h5 class="section-panel__title">
                {{ $t("Scholarship & Status") }}
              </h5>
            </div>
            <b-row class="section-panel__body">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Scholarship Category')"
                  label-class="field-label field-label--required"
                >
                  <v-select
                    v-model="form.studentDetails.scholarshipCategory"
                    :options="scholarshipCategoryOptions"
                    :clearable="false"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select scholarship category')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12" class="form-field">
                <b-form-group :label="$t('Comment')" label-class="field-label">
                  <b-form-textarea
                    v-model.trim="form.studentDetails.comment"
                    class="form-textarea-custom"
                    rows="3"
                    :placeholder="$t('Add any notes or comments')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group :label="$t('Tag')" label-class="field-label">
                  <v-select
                    v-model="form.student.house"
                    :options="tagOptions"
                    :reduce="(o) => o.value"
                    label="label"
                    :clearable="false"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select tag')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12" class="form-field">
                <b-form-group
                  :label="$t('Tag Comment')"
                  label-class="field-label"
                >
                  <b-form-textarea
                    v-model.trim="form.student.last_institute"
                    class="form-textarea-custom"
                    rows="3"
                    :placeholder="$t('Add tag comments')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="section-panel">
            <div class="section-panel__head">
              <span class="section-panel__icon">
                <feather-icon icon="AwardIcon" size="16" />
              </span>
              <h5 class="section-panel__title">
                {{ $t("UTP") }}
              </h5>
            </div>
            <b-row class="section-panel__body">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('UTP Support')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpSupport"
                    :options="utpSupportOptions"
                    :reduce="(o) => o.value"
                    label="label"
                    :clearable="false"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select UTP support')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('UTP Fiscal Year')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpFiscalYear"
                    :options="utpFiscalOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select UTP fiscal year')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('UTP Course')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpCourse"
                    :options="utpCourseOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select UTP course')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Mode of Class')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpClassMode"
                    :options="utpClassModeOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select mode of class')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="8" sm="6" class="form-field">
                <b-form-group
                  :label="$t('UTP Institute')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="selectedUtpInstitute"
                    :options="utpCampuses"
                    label="name"
                    :clearable="false"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select Institute')"
                  >
                    <template #option="option">
                      <div
                        v-b-tooltip.hover.topright
                        :title="`${option.name} (${option.branchCode})`"
                      >
                        <div>{{ option.name }}</div>
                        <div class="font-weight-bolder">
                          {{ option.branchCode }}
                        </div>
                      </div>
                    </template>
                    <template #selected-option="option">
                      <div>
                        <div>{{ option.name }}</div>
                        <div class="font-weight-bolder">
                          {{ option.branchCode }}
                        </div>
                      </div>
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('1. Locked Program')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedProgram1"
                    :options="utpProgramOptions"
                    :clearable="true"
                    :reduce="(o) => o.title"
                    label="title"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Enter locked program 1')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('1. Locked Tier')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedTier1"
                    :options="utpLockedTierOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select locked tier 1')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('2. Locked Program')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedProgram2"
                    :options="utpProgramOptions"
                    :clearable="true"
                    :reduce="(o) => o.title"
                    label="title"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Enter locked program 2')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('2. Locked Tier')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedTier2"
                    :options="utpLockedTierOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select locked tier 2')"
                  />
                </b-form-group>
              </b-col>
              <!-- <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('3. Locked Program')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedProgram3"
                    :options="utpProgramOptions"
                    :reduce="(o) => o.title"
                    label="title"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Enter locked program 3')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('3. Locked Tier')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpLockedTier3"
                    :options="utpLockedTierOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select locked tier 3')"
                  />
                </b-form-group>
              </b-col> -->
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Qarz-e-hasna Form Status')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpQarzehasnaStatus"
                    :options="utpQarzehasnaStatusOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select Qarz-e-hasna status')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12" class="form-field">
                <b-form-group
                  :label="$t('UTP Comment')"
                  label-class="field-label"
                >
                  <b-form-textarea
                    v-model.trim="form.studentDetails.utpComment"
                    class="form-textarea-custom"
                    rows="3"
                    :placeholder="$t('Add UTP comment')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row  class="section-panel__body pt-0">
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Admission Status')"
                  label-class="field-label"
                >
                  <v-select
                    v-model="form.studentDetails.utpAdmissionStatus"
                    :options="admissionStatusOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select admission status')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('University')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.utpAdmissionUniversity"
                    class="form-input-custom"
                    :placeholder="$t('Enter university')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Program')"
                  label-class="field-label"
                >
                   <v-select
                    v-model="form.studentDetails.utpAdmissionProgram"
                    :options="utpProgramOptions"
                    :clearable="true"
                    :reduce="(o) => o.title"
                    label="title"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Enter program')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Discipline')"
                  label-class="field-label"
                >
                  <b-form-input
                    v-model.trim="form.studentDetails.utpAdmissionDiscipline"
                    class="form-input-custom"
                    :placeholder="$t('Enter discipline')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Tier')"
                  label-class="field-label"
                >
                   <v-select
                    v-model="form.studentDetails.utpAdmissionTier"
                    :options="utpLockedTierOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select tier')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" sm="6" class="form-field">
                <b-form-group
                  :label="$t('Fiscal Year')"
                  label-class="field-label"
                >
                   <v-select
                    v-model="form.studentDetails.utpAdmissionFiscalYear"
                    :options="utpFiscalOptions"
                    :clearable="true"
                    append-to-body
                    class="form-select-custom"
                    :placeholder="$t('Select fiscal year')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="form-actions">
            <b-button
              variant="outline-secondary"
              class="action-btn"
              @click="goBack"
            >
              {{ $t("Cancel") }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              class="action-btn action-btn--primary"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              {{ saveLabel }}
            </b-button>
          </div>
        </b-form>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BSpinner,
  BAvatar,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";

function emptyStudentDetails() {
  return {
    id: 0,
    sID: 0,
    campusID: 0,
    region: "",
    area: "",
    federalRegNo: "",
    matricRollNo: "",
    school: "",
    matricBoard: "",
    ninthPerc: "",
    hsstPerc: "",
    overallPerc: "",
    hsstStatus: "",
    matricPerc: "",
    matricGrade: "",
    studentCnic: "",
    scholarshipCategory: "",
    instituteName: "",
    branchCode: "",
    comment: "",
    scholarshipStatus: "N/A",
    studentStatus: "N/A",
    interFaculty: "",
    firstYearRollNo: "",
    intermediateBoard: "",
    firstYearPerc: "",
    paperPassed: "",
    secondYearRollNo: "",
    secondYearPerc: "",
    college: "",
    fiscalYear: "",
    schoolID: "",
    supportedYears: 0,
    utpSupport: "",
    utpFiscalYear: "",
    utpCourse: "",
    utpLockedProgram1: "",
    utpLockedTier1: "",
    utpLockedProgram2: "",
    utpLockedTier2: "",
    utpLockedProgram3: "",
    utpLockedTier3: "",
    utpInstituteID: "",
    utpInstituteName: "",
    utpClassMode: "",
    utpQarzehasnaStatus: "",
    utpStatus: "",
    utpComment: "",
    utpAdmissionStatus: "",
    utpAdmissionUniversity: "",
    utpAdmissionProgram: "",
    utpAdmissionDiscipline: "",
    utpAdmissionTier: "",
    utpAdmissionFiscalYear: "",
    utpAdmissionRemarks: ""
  };
}

function emptyStudent() {
  return {
    name: "",
    father_name: "",
    gender: "",
    contact_student: "",
    contact_father: "",
    contact_mother: "",
    class_name: "",
    section_name: "",
    picture: "",
    emailAddress: "",
    last_result: "",
    house: "",
    last_institute: "",
  };
}

function resolveClassAndSection(student) {
  if (!student || typeof student !== "object") {
    return { className: "", sectionName: "", sectionId: null };
  }

  let className = student.class_name || "";
  let sectionName =
    student.section_name || student.meta_section || student.sectionName || "";

  const sectionId =
    student.section_id || student.sectionID || student.secID || null;

  const metaClass = String(student.meta_class || "").trim();
  if (metaClass.includes(" - ")) {
    const parts = metaClass.split(" - ");
    if (!className) className = (parts[0] || "").trim();
    if (!sectionName) {
      sectionName = parts.slice(1).join(" - ").trim();
    }
  } else if (!className && metaClass) {
    className = metaClass;
  }

  return { className, sectionName, sectionId };
}

function normalizeSectionId(id) {
  if (id === null || id === undefined || id === "") return null;
  const n = Number(id);
  return Number.isNaN(n) ? id : n;
}

export default {
  name: "StudentTcfForm",
  props: {
    selected: { type: String, default: "" },
    id: { type: [String, Number], default: 0 },
    inqObj: { type: Object, default: null },
  },
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BSpinner,
    BAvatar,
    vSelect,
  },
  data() {
    return {
      uRights: null,
      pageLoading: true,
      saving: false,
      photoLoading: false,
      defaultPhotoPlaceholder:
        "https://cdn.cloudious.net/file-1677912450367-333289208.png",
      classes: [],
      selectedClassId: null,
      selectedSectionId: null,
      restoringClassSection: false,
      form: {
        student: emptyStudent(),
        studentDetails: emptyStudentDetails(),
      },
      genderOptions: [
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
      ],
      tagOptions: [
        { label: "Green", value: "green" },
        { label: "Yellow", value: "yellow" },
        { label: "Red", value: "red" },
      ],
      scholarshipCategoryOptions: [
        "Offered Coaching",
        "Offered Pvt College",
        "Offered TCF College",
        "Offered PCP",
        "Not Offered",
      ],
      utpSupportOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      utpFiscalOptions: ["2026-27", "2025-26", "2024-25", "2023-24"],
      utpCourseOptions: [
        "MDCAT",
        "LMDCAT + AKU Prep",
        "ECAT",
        "FUNG",
        "LFUNG",
        "BCAT",
        "LAT",
        "NAT",
      ],
      utpLockedTierOptions: ["Tier 1", "Tier 2", "Tier 3", "Tier 4 & Below"],
      utpClassModeOptions: ["Online", "In-person"],
      utpQarzehasnaStatusOptions: ["Submitted", "Not Submitted"],
      admissionStatusOptions: ["Enrolled", "Not Enrolled"],
      utpProgramOptions: [],
      selectedUtpInstitute: null,
      utpCampuses: [],
    };
  },
  computed: {
    studentId() {
      return parseInt(this.$route.params.id, 10) || 0;
    },
    isEdit() {
      return this.studentId > 0;
    },
    pageTitle() {
      if (this.isEdit) return this.$t("Update Student");
      if (this.selected === "reg") return this.$t("Registration");
      return this.$t("Admission");
    },
    pageSubtitle() {
      if (this.isEdit) {
        return this.$t("Update student profile information");
      }
      if (this.selected === "reg") {
        return this.$t("Register a new student");
      }
      return this.$t("Add a new student");
    },
    saveLabel() {
      if (this.isEdit) return this.$t("Save");
      if (this.selected === "reg") return this.$t("Register");
      return this.$t("Save");
    },
    classOptions() {
      return this.classes.map((item) => ({
        id: item.cls.id,
        label: item.cls.meta_class || item.cls.name,
        raw: item,
      }));
    },
    sectionOptions() {
      const cls = this.classes.find(
        (item) => item.cls && item.cls.id === this.selectedClassId
      );
      if (!cls || !Array.isArray(cls.sections)) return [];
      return cls.sections.map((sec) => ({
        id: normalizeSectionId(sec.id),
        label: sec.section || sec.name || "",
      }));
    },
    photoPreviewSrc() {
      return this.form.student.picture || this.defaultPhotoPlaceholder;
    },
  },
  async created() {
    if (!this.checkAccess()) return;
    await this.loadClasses();
    this.LoadPrograms();
    if (this.isEdit) {
      await this.loadStudent();
    } else {
      // on edit this load is called after load student which is fine
      this.LoadUtpCampuses();
      this.form.studentDetails.campusID = this.$store.state.userData.cId;
    }
    this.pageLoading = false;
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put" }),

    checkAccess() {
      const st = this.$route.query.status;
      let right = null;

      if (this.studentId === 0) {
        right = this.$store.state.rights["manage-studentgrid"];
      } else if (st) {
        if (
          ["present", "registered", "left", "enrolled", "alumni"].includes(st)
        ) {
          right = this.$store.state.rights["manage-studentgrid"];
        }
      } else {
        right = this.$store.state.rights["manage-studentgrid"];
      }

      this.uRights = right;
      if (
        !right ||
        (this.studentId === 0 && !right.add) ||
        (this.studentId > 0 && !right.edit)
      ) {
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }
      return true;
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: "manage-studentgrid" });
      }
    },

    async LoadPrograms() {
      var obj = {
        url:
          this.$store.state.domain +
          "DropdownList/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&category=utpPrograms",
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      this.utpProgramOptions = Array.isArray(res) ? res : [];
    },
    async LoadUtpCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "CampusDetails/LoadData?db=" +
          this.$store.state.userData.db +
          "&isUtp=true",
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      this.utpCampuses = Array.isArray(res) ? res : [];
    },

    async loadClasses(campusID) {
      const cID = campusID || this.$store.state.userData.cId;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            cID +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        this.classes = Array.isArray(res)
          ? res.map((el) =>
              el.cls ? el : { cls: el, sections: el.sections || [] }
            )
          : [];
      } catch (e) {
        console.error("LoadClasses error:", e);
        this.classes = [];
      }
    },

    matchClassSelection(className, sectionName, sectionId) {
      if (!className) return;
      const normalizedClass = String(className).trim().toLowerCase();
      const match = this.classes.find((item) => {
        if (!item.cls) return false;
        const candidates = [
          item.cls.name,
          item.cls.meta_class,
          item.cls.metaClass,
        ]
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase());
        return candidates.some(
          (c) =>
            c === normalizedClass ||
            normalizedClass.includes(c) ||
            c.includes(normalizedClass)
        );
      });
      if (!match || !match.cls) return;

      this.restoringClassSection = true;
      this.selectedClassId = match.cls.id;

      if (Array.isArray(match.sections)) {
        let sec = null;
        const normalizedSectionId = normalizeSectionId(sectionId);

        if (normalizedSectionId !== null) {
          sec = match.sections.find(
            (s) => normalizeSectionId(s.id) === normalizedSectionId
          );
        }

        if (!sec && sectionName) {
          const normalizedSection = String(sectionName).trim().toLowerCase();
          sec = match.sections.find((s) => {
            const candidates = [s.section, s.name, s.meta_section]
              .filter(Boolean)
              .map((v) => String(v).trim().toLowerCase());
            return candidates.some(
              (c) =>
                c === normalizedSection ||
                normalizedSection.includes(c) ||
                c.includes(normalizedSection)
            );
          });
        }

        this.selectedSectionId = sec ? normalizeSectionId(sec.id) : null;
      }

      this.$nextTick(() => {
        this.restoringClassSection = false;
      });
    },

    async loadStudent() {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/GetByStudentID?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          this.studentId,
        token: this.$store.state.userData.token,
        context: this,
      });

      const student = res && res.student;
      const details = res && res.studentDetails;
      if (!student || !details) {
        this.$bvToast.toast(this.$t("Invalid student data received."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.goBack();
        return;
      }

      const campusID =
        student.campusID || details.campusID || this.$store.state.userData.cId;
      const { className, sectionName, sectionId } =
        resolveClassAndSection(student);

      if (campusID !== this.$store.state.userData.cId) {
        await this.loadClasses(campusID);
      }

      this.selectedClassId = student.class_current || null;
      this.selectedSectionId = student.section_current || null;

      this.form.student = {
        ...emptyStudent(),
        name: student.name || "",
        father_name: student.father_name || "",
        gender: student.gender || "",
        contact_student: student.contact_student || "",
        contact_father: student.contact_father || "",
        contact_mother: student.contact_mother || "",
        class_name: className || student.class_name || student.meta_class || "",
        section_name: sectionName,
        picture: student.picture || "",
        emailAddress: student.emailAddress || "",
        last_result: student.last_result || "",
        house: (student.house || "").toLowerCase(),
        last_institute: student.last_institute || "",
      };

      this.form.studentDetails = {
        ...emptyStudentDetails(),
        ...details,
        sID: this.studentId,
        campusID,
      };

      if (this.form.studentDetails.utpSupport) {
        this.form.studentDetails.utpSupport =
          this.form.studentDetails.utpSupport.toLowerCase();
      }

      await this.LoadUtpCampuses();
      this.$nextTick(() => {
        if (this.form.studentDetails.utpInstituteID) {
          const instId = this.form.studentDetails.utpInstituteID;
          const match = this.utpCampuses.find(
            (c) =>
              String(c.branchCode).trim().toLowerCase() ===
              String(instId).trim().toLowerCase()
          );
          if (match) this.selectedUtpInstitute = match;
        }
      });

      // this.matchClassSelection(className, sectionName, sectionId);
    },

    onClassChange() {
      if (this.restoringClassSection) return;
      this.selectedSectionId = null;
    },

    triggerPhotoUpload() {
      if (this.photoLoading) return;
      if (this.$refs.photoInput) {
        this.$refs.photoInput.click();
      }
    },

    removePhoto() {
      this.form.student.picture = "";
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = "";
      }
    },

    onPhotoSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$bvToast.toast(this.$t("Please select a valid image file."), {
          title: this.$t("Invalid file"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$bvToast.toast(this.$t("Image must be smaller than 5MB."), {
          title: this.$t("File too large"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        event.target.value = "";
        return;
      }

      this.uploadPhoto(file);
    },

    async uploadPhoto(file) {
      this.photoLoading = true;

      try {
        const uploaded = await uploadToMySkool([file]);
        const path = getUploadPath(uploaded[0]);

        if (!path) {
          throw new Error("Upload response missing image path");
        }

        this.form.student.picture = path;
        this.$bvToast.toast(this.$t("Photo uploaded successfully."), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
      } catch (e) {
        console.error("Photo upload error:", e);
        this.$bvToast.toast(this.$t("Photo upload failed. Please try again."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.photoLoading = false;
        if (this.$refs.photoInput) {
          this.$refs.photoInput.value = "";
        }
      }
    },

    async updateStudentPicture() {
      if (!this.isEdit || !this.form.student.picture) return true;

      try {
        const status = await this.put({
          url:
            this.$store.state.domain +
            "Students/UpdatePicture?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            this.studentId +
            "&picture=" +
            encodeURIComponent(this.form.student.picture),
          context: this,
          token: this.$store.state.userData.token,
        });
        return status !== false;
      } catch (e) {
        console.error("UpdatePicture error:", e);
        return false;
      }
    },

    toDecimal(val) {
      if (val === null || val === undefined || val === "") return 0;
      const cleaned = String(val).replace(/%/g, "").trim();
      const n = parseFloat(cleaned);
      return Number.isNaN(n) ? 0 : n;
    },

    getClassName() {
      const match = this.classes.find(
        (item) => item.cls && item.cls.id === this.selectedClassId
      );
      if (!match || !match.cls) return this.form.student.class_name || "";
      return match.cls.meta_class || match.cls.name || "";
    },

    getSectionName() {
      const match = this.classes.find(
        (item) => item.cls && item.cls.id === this.selectedClassId
      );
      if (!match || !Array.isArray(match.sections) || !this.selectedSectionId) {
        return this.form.student.section_name || "";
      }
      const sec = match.sections.find(
        (s) =>
          normalizeSectionId(s.id) ===
          normalizeSectionId(this.selectedSectionId)
      );
      return sec ? sec.section || sec.name || "" : "";
    },

    validateForm() {
      const s = this.form.student;
      const d = this.form.studentDetails;
      const missing = [];

      if (!d.instituteName) missing.push(this.$t("Institute Name"));
      if (!s.contact_student) missing.push(this.$t("Primary Contact"));
      if (!s.contact_father) missing.push(this.$t("WhatsApp Number"));
      if (!d.branchCode) missing.push(this.$t("Branch Code"));
      if (!d.federalRegNo) {
        missing.push(this.$t("Federal Board Registration No"));
      }
      if (!d.scholarshipCategory) {
        missing.push(this.$t("Scholarship Category"));
      }

      if (missing.length) {
        this.$bvToast.toast(`${this.$t("Missing")}: ${missing.join(", ")}`, {
          title: this.$t("Validation"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      return true;
    },

    buildPayload() {
      const d = this.form.studentDetails;
      return {
        student: {
          name: this.form.student.name,
          father_name: this.form.student.father_name,
          gender: this.form.student.gender,
          contact_student: this.form.student.contact_student,
          contact_father: this.form.student.contact_father,
          contact_mother: this.form.student.contact_mother,
          class_name: this.getClassName(),
          section_name: this.getSectionName(),
          picture: this.form.student.picture || "",
          emailAddress: this.form.student.emailAddress || "",
          last_result: this.form.student.last_result || "",
          house: this.form.student.house || "",
          last_institute: this.form.student.last_institute || "",
        },
        studentDetails: {
          id: d.id || 0,
          sID: this.isEdit ? this.studentId : 0,
          campusID: d.campusID || this.$store.state.userData.cId,
          region: d.region,
          area: d.area,
          federalRegNo: d.federalRegNo,
          matricRollNo: d.matricRollNo,
          school: d.school,
          matricBoard: d.matricBoard,
          ninthPerc: this.toDecimal(d.ninthPerc),
          hsstPerc: this.toDecimal(d.hsstPerc),
          overallPerc: this.toDecimal(d.overallPerc),
          hsstStatus: d.hsstStatus,
          matricPerc: this.toDecimal(d.matricPerc),
          matricGrade: d.matricGrade,
          studentCnic: d.studentCnic,
          scholarshipCategory: d.scholarshipCategory,
          instituteName: d.instituteName,
          branchCode: d.branchCode,
          comment: d.comment,
          scholarshipStatus: d.scholarshipStatus || "N/A",
          studentStatus: d.studentStatus || "N/A",
          interFaculty: d.interFaculty,
          firstYearRollNo: d.firstYearRollNo,
          intermediateBoard: d.intermediateBoard,
          firstYearPerc: this.toDecimal(d.firstYearPerc),
          paperPassed: d.paperPassed,
          secondYearRollNo: d.secondYearRollNo,
          secondYearPerc: this.toDecimal(d.secondYearPerc),
          college: d.college,
          fiscalYear: d.fiscalYear,
          schoolID: d.schoolID,
          supportedYears: d.supportedYears,
          utpSupport: d.utpSupport ? d.utpSupport.toLowerCase() : "",
          utpFiscalYear: d.utpFiscalYear,
          utpCourse: d.utpCourse,
          utpLockedProgram1: d.utpLockedProgram1,
          utpLockedTier1: d.utpLockedTier1,
          utpLockedProgram2: d.utpLockedProgram2,
          utpLockedTier2: d.utpLockedTier2,
          utpLockedProgram3: d.utpLockedProgram3,
          utpLockedTier3: d.utpLockedTier3,
          utpInstituteID: this.selectedUtpInstitute
            ? this.selectedUtpInstitute.branchCode
            : d.utpInstituteID,
          utpInstituteName: this.selectedUtpInstitute
            ? this.selectedUtpInstitute.name
            : d.utpInstituteName,
          utpClassMode: d.utpClassMode,
          utpQarzehasnaStatus: d.utpQarzehasnaStatus,
          utpStatus: d.utpStatus,
          utpComment: d.utpComment,
          utpAdmissionStatus: d.utpAdmissionStatus,
          utpAdmissionUniversity: d.utpAdmissionUniversity,
          utpAdmissionProgram: d.utpAdmissionProgram,
          utpAdmissionDiscipline: d.utpAdmissionDiscipline,
          utpAdmissionTier: d.utpAdmissionTier,
          utpAdmissionFiscalYear: d.utpAdmissionFiscalYear,
          utpAdmissionRemarks: d.utpAdmissionRemarks,
        },
      };
    },

    async saveStudent() {
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        const response = await this.post({
          url:
            this.$store.state.domain +
            "StudentDetails/BulkUpload?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: [this.buildPayload()],
          context: this,
          isBulkUpload: true,
          token: this.$store.state.userData.token,
        });

        if (!response) {
          this.$bvToast.toast(this.$t("Save failed."), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
          return;
        }

        if (response.status === "error") {
          this.$bvToast.toast(this.$t("Validation failed."), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
          return;
        }

        if (this.isEdit && this.form.student.picture) {
          await this.updateStudentPicture();
        }

        const jid =
          response.jobID ||
          response.jobId ||
          (response.data && (response.data.jobID || response.data.jobId));

        if (jid) {
          this.$bvToast.toast(this.$t("Student saved successfully."), {
            title: this.$t("Success"),
            variant: "success",
            toaster: "b-toaster-top-center",
          });
          this.goBack();
          return;
        }

        this.$bvToast.toast(this.$t("Student saved successfully."), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        this.goBack();
      } catch (e) {
        console.error("Save TCF student error:", e);
        this.$bvToast.toast(this.$t("Save failed."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.tcf-student-form {
  padding: 0.75rem;
  max-width: 1200px;
  margin: 0 auto;
}

.student-card {
  background: #ffffff;
  border: 1px solid #e8ecf1;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.form-header {
  gap: 0.75rem;
  padding-bottom: 1.25rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #eef2f6;
}

.form-page-title {
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  font-size: 1.35rem;
}

.back-btn {
  border-radius: 10px;
  font-weight: 600;
  min-width: 88px;
}

.admission-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-panel {
  background: #fafbfc;
  border: 1px solid #eef2f6;
  border-radius: 14px;
  overflow: visible;
}

.section-panel__head {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  background: #ffffff;
  border-bottom: 1px solid #eef2f6;
}

.section-panel__icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-panel__title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1e293b;
}

.section-panel__body {
  padding: 1rem 0.85rem 0.35rem;
  margin: 0;
}

.section-panel__body--compact {
  padding-top: 0.35rem;
}

.section-panel__body--photo {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eef2f6;
}

.photo-upload {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.photo-upload__avatar-wrap {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  cursor: pointer;
  overflow: visible;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

::v-deep .photo-upload__avatar {
  width: 120px !important;
  height: 120px !important;
  border: 3px solid #ffffff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.1);
  background: #f1f5f9 !important;
}

::v-deep .photo-upload__avatar .b-avatar-img {
  object-fit: cover;
}

.photo-upload__badge {
  position: absolute;
  right: -4px;
  bottom: -4px;
  z-index: 2;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background: #7367f0;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  pointer-events: none;
}

.photo-upload__loading {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-upload__meta {
  flex: 1;
  min-width: 180px;
}

.photo-upload__title {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

.photo-upload__hint {
  margin: 0 0 0.65rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.photo-upload__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.photo-upload__link {
  border: none;
  background: none;
  padding: 0;
  font-size: 0.84rem;
  font-weight: 600;
  color: #7367f0;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: #5e50ee;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.photo-upload__link--danger {
  color: #ef4444;

  &:hover:not(:disabled) {
    color: #dc2626;
  }
}

.form-field {
  margin-bottom: 0.85rem;
}

::v-deep .field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.4rem;
  letter-spacing: 0.01em;
}

::v-deep .field-label--required::after {
  content: " *";
  color: #ef4444;
  font-weight: 700;
}

.form-input-custom,
.form-textarea-custom,
::v-deep .section-panel__body .form-control {
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid #dbe3ec;
  background: #ffffff;
  font-size: 0.92rem;
  color: #0f172a;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #94a3b8;
    font-size: 0.88rem;
  }

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    background: #ffffff;
  }
}

.form-textarea-custom {
  min-height: 96px;
  padding-top: 0.65rem;
}

.form-input-custom--disabled,
::v-deep .form-input-custom--disabled.form-control:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}

::v-deep .form-select-custom .vs__dropdown-toggle {
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid #dbe3ec;
  background: #ffffff;
  padding: 4px 10px;
  font-size: 0.92rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

::v-deep .form-select-custom.vs--open .vs__dropdown-toggle,
::v-deep .form-select-custom.vs--focused .vs__dropdown-toggle {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
}

::v-deep .form-select-custom .vs__selected {
  color: #0f172a;
  font-weight: 500;
  font-size: 0.92rem;
}

::v-deep .form-select-custom .vs__search::placeholder,
::v-deep .form-select-custom .vs__placeholder {
  color: #94a3b8;
  font-size: 0.88rem;
}

::v-deep .form-select-custom .vs__dropdown-menu {
  border-radius: 10px;
  border: 1px solid #dbe3ec;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 6px;
  margin-top: 4px;
}

::v-deep .form-select-custom .vs__dropdown-option {
  border-radius: 8px;
  padding: 9px 12px;
  margin-bottom: 2px;
  font-size: 0.9rem;
}

::v-deep .form-select-custom .vs__dropdown-option--highlight {
  background: #f4f2ff;
  color: #5b21b6;
}

::v-deep .form-select-custom.vs--disabled .vs__dropdown-toggle {
  background: #f8fafc;
  opacity: 1;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 0 0.25rem;
  margin-top: 0.25rem;
  border-top: 1px solid #eef2f6;
}

.action-btn {
  min-width: 130px;
  border-radius: 10px;
  font-weight: 600;
  padding: 0.6rem 1.25rem;
}

.action-btn--primary {
  box-shadow: 0 6px 16px rgba(115, 103, 240, 0.22);
}
</style>
